import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import MarketId from 'components/shared/MarketId';
import SVG from 'components/icons/SVG';
import SearchInput from 'components/shared/SearchInput';
import ButtonText from 'components/shared/ButtonText';
import { truncate } from 'helpers/assets';
import { isNaNish } from 'helpers/balances';
import { useTickers } from 'helpers/hooks';
import { useSortedTickers } from 'helpers/hooks/useSortedTickers';
import styles from './styles.module.scss';

const columnWidths = [4, 4, 4];
const endAlignIndex = 1;

const microapps = window.MICROFRONTENDS_CONFIG;

function MarketsPopover(props) {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState(true);
  const [filter, setFilter] = useState('');
  const [cachedTickers, setCachedTickers] = useState([]);
  const tickers = useTickers({ vetoCondition: !props.isVisible });
  const headers = [
    () => (
      <div className={styles.paddedLeft}>
        <Text muted styles={[styles.wideText]}>
          {t('common_market')}
        </Text>
      </div>
    ),
    () => (
      <Text muted rightAligned styles={[styles.wideText]}>
        {t('market_last_price')}
      </Text>
    ),
    () => (
      <div className={styles.paddedRight}>
        <Text muted rightAligned styles={[styles.wideText]}>
          {t('market_24h_change')}
        </Text>
      </div>
    )
  ];

  useEffect(() => {
    if (tickers.data.length > 0 && !tickers.loading && !tickers.error) setCachedTickers(tickers.data);
  }, [tickers.loading, tickers.error, tickers.data]);

  useEffect(
    () => {
      document.addEventListener('mousedown', onMouseDown);
      return () => document.removeEventListener('mousedown', onMouseDown);
    },
    [] // eslint-disable-line
  );

  const onMouseDown = useCallback(
    e => {
      if (e.target.id === 'markets-popover' || e.target.id === 'add-tab-button') return;
      if (!props.popoverRef.current.contains(e.target)) {
        props.closePopover();
      }
    },
    [] // eslint-disable-line
  );

  const handleChangeFilter = e => {
    setFilter(e.target.value);
  };

  const filterTickers = market => {
    const pinned = props.pinnedMarkets.includes(market.data.pair);
    let inFilter = true;
    if (filter !== '') {
      const lcFilter = filter.toLowerCase();
      const pair = market.data.name.toLowerCase();
      inFilter = pair.includes(lcFilter) || lcFilter.includes(pair);
    }
    return !pinned && inFilter;
  };

  const getChangeColour = (change, isChangeUp) => {
    if (change === undefined) return 'var(--text)';
    return isChangeUp ? 'var(--color-green)' : 'var(--color-red)';
  };
  const renderItem = useCallback((ticker, index) => {
    const priceFloat = truncate(ticker.price, ticker.minTickLength);
    return {
      0: (
        <div className={styles.paddedLeft}>
          <MarketId pair={ticker.id} iconSize="26" />
        </div>
      ),
      1: <Text>{!isNaNish(ticker.price) ? `${priceFloat}` : '-'}</Text>,
      2: (
        <div className={styles.paddedRight}>
          <Text color={getChangeColour(ticker.change, ticker.isChangeUp)} styles={[styles.wideText]}>
            {ticker.change !== undefined ? `${truncate(parseFloat(ticker.change), 2)}%` : '-'}
          </Text>
        </div>
      )
    }[index];
  }, []);
  const renderLink = useCallback(
    market => `${microapps.routes.trading.path.split(':')[0]}${market.pair}`,
    []
  );

  const handleClickRow = market => {
    setAnimate(false);
    props.closePopover();
    props.onClickCallback(market);
    setTimeout(() => {
      setAnimate(true);
    });
  };

  const sortedTickers = useSortedTickers({
    tickers: cachedTickers,
    filter: filterTickers
  });

  const renderContents = () => {
    if (tickers.error) {
      return (
        <div className={styles.noSearchResultsContainer}>
          <Text styles={[styles.noSearchResults]}>{t('market_something_went_wrong_while_fetching')}</Text>
          <ButtonText underlined onClick={tickers.refetch}>
            {t('common_try_again')}
          </ButtonText>
        </div>
      );
    }
    if (sortedTickers.length > 0 || tickers.loading || props.preferences.loading) {
      return (
        <div className={styles.tableContainer}>
          <Table
            data={sortedTickers}
            keyField="id"
            refetchData={tickers.refetch}
            isLoading={cachedTickers.length === 0 && (tickers.loading || props.preferences.loading)}
            error={tickers.error}
            headers={headers}
            headerStyles={[styles.tableHeader]}
            hasMaxHeight
            columnWidths={columnWidths}
            endAlignIndex={endAlignIndex}
            highlightRowOnHover
            renderItem={renderItem}
            renderLink={renderLink}
            rowHeight={50}
            onClickRow={handleClickRow}
            borders={false}
          />
        </div>
      );
    }
    return (
      <div className={styles.noSearchResultsContainer}>
        <Text styles={[styles.noSearchResults]}>{t('market_no_markets_match_query')}</Text>
      </div>
    );
  };

  return (
    <div className={styles.popoverContainer}>
      <div
        className={classnames([
          styles.popoverBox,
          {
            [styles.visiblePopover]: props.isVisible,
            [styles.noAnimation]: !animate
          }
        ])}
        ref={props.popoverRef}
        id="markets-popover">
        <div className={styles.popoverHeader}>
          <div>
            <SVG icon="plus" styles={[styles.hiddenPlus]} />
            <Text styles={[styles.popoverHeaderText]}>{t('market_select_markets')}</Text>
          </div>
          <SearchInput
            id="marketFilter"
            name={t('market_filter_markets')}
            noLabel
            onChange={handleChangeFilter}
            value={filter}
          />
        </div>
        {renderContents()}
      </div>
    </div>
  );
}

MarketsPopover.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  preferences: PropTypes.object.isRequired, // eslint-disable-line
  pinnedMarkets: PropTypes.array.isRequired,
  closePopover: PropTypes.func.isRequired,
  onClickCallback: PropTypes.func.isRequired,
  popoverRef: PropTypes.shape({ current: PropTypes.any })
};

MarketsPopover.defaultProps = {
  popoverRef: null
};

const compareProps = (prevProps, nextProps) => {
  return (
    prevProps.isVisible === nextProps.isVisible &&
    JSON.stringify(prevProps.preferences) === JSON.stringify(nextProps.preferences) &&
    JSON.stringify(prevProps.pinnedMarkets) === JSON.stringify(nextProps.pinnedMarkets.length)
  );
};

export default memo(MarketsPopover, compareProps);
